import React, { useState, useEffect } from 'react';
import { Input, Button, message, Card, Table, Image, Space, Popconfirm, Pagination, Alert, Modal, Switch, Tooltip, Progress } from 'antd';
import {
  PlusOutlined,
  MinusCircleOutlined,
  DeleteOutlined,
  ShoppingOutlined,
  LinkOutlined,
  LoadingOutlined,
  VideoCameraOutlined,
  CopyOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  MergeCellsOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './index.css';

const { TextArea } = Input;

const API_BASE = '/api';

function Product() {
  const [productInputs, setProductInputs] = useState(['']);
  const [addProgress, setAddProgress] = useState({ total: 0, completed: 0, success: 0, failed: 0, results: [] });
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [fetching, setFetching] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [productVideos, setProductVideos] = useState({});  // { productId: [videos] }
  const navigate = useNavigate();

  // 打开添加弹窗
  const openAddModal = () => {
    setProductInputs(['']);
    setAddProgress({ total: 0, completed: 0, success: 0, failed: 0, results: [] });
    setModalVisible(true);
  };

  const resetAddModal = () => {
    if (loading) return;
    setModalVisible(false);
    setProductInputs(['']);
    setAddProgress({ total: 0, completed: 0, success: 0, failed: 0, results: [] });
  };

  const addProductInput = () => {
    setProductInputs(prev => [...prev, '']);
  };

  const updateProductInput = (index, value) => {
    setProductInputs(prev => prev.map((item, i) => (i === index ? value : item)));
  };

  const removeProductInput = (index) => {
    setProductInputs(prev => (prev.length <= 1 ? [''] : prev.filter((_, i) => i !== index)));
  };
  const loadProducts = async (page = 1) => {
    try {
      setFetching(true);
      const res = await axios.get(`${API_BASE}/products?page=${page}&pageSize=${pagination.pageSize}`);
      if (res.data) {
        setProducts(res.data.items || []);
        setPagination(prev => ({
          ...prev,
          current: res.data.page,
          total: res.data.total
        }));
      }
    } catch (err) {
      console.error('加载商品列表失败', err);
      message.error('加载商品列表失败');
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    loadProducts(1);
  }, []);

  // 添加商品
  const handleAddProduct = async () => {
    const tasks = productInputs.map(text => text.trim()).filter(Boolean);
    if (tasks.length === 0) {
      message.warning('请输入商品链接');
      return;
    }

    setLoading(true);
    setAddProgress({ total: tasks.length, completed: 0, success: 0, failed: 0, results: [] });

    let success = 0;
    let failed = 0;
    const results = [];
    try {
      // 顺序执行，避免多个商品同时抓图/生成封面导致后端和第三方接口压力过大
      for (let i = 0; i < tasks.length; i += 1) {
        const taskText = tasks[i];
        try {
          const res = await axios.post(`${API_BASE}/products`, { text: taskText });
          if (res.data.success) {
            success += 1;
            results.push({ index: i + 1, status: 'success', title: res.data.data?.title || '商品添加成功' });
          } else {
            failed += 1;
            results.push({ index: i + 1, status: 'error', title: res.data.error || '添加失败' });
          }
        } catch (err) {
          failed += 1;
          results.push({
            index: i + 1,
            status: 'error',
            title: err.response?.data?.error || '添加失败'
          });
          console.error(`添加第 ${i + 1} 个商品失败`, err);
        }

        setAddProgress({
          total: tasks.length,
          completed: i + 1,
          success,
          failed,
          results: [...results]
        });
      }

      if (success > 0) {
        message.success(`批量添加完成：成功 ${success} 个，失败 ${failed} 个`);
        await loadProducts(1);
      } else {
        message.error('批量添加失败，请检查商品链接');
      }

      if (failed === 0) {
        setProductInputs(['']);
        setModalVisible(false);
      }
    } catch (err) {
      console.error('批量添加商品失败', err);
      message.error('批量添加失败');
    } finally {
      setLoading(false);
    }
  };

  // 删除商品
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE}/products/${id}`);
      message.success('删除成功');
      loadProducts(pagination.current);
    } catch (err) {
      console.error('删除商品失败', err);
      message.error('删除失败');
    }
  };

  // 复制文本到粘贴板
  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(`${label}已复制`);
    }).catch(() => {
      message.error('复制失败');
    });
  };

  // 跳转到gen2页面生成视频
  const handleGenerateVideo = (record) => {
    navigate(`/gen2?productId=${record.id}`);
  };

  // 分页变化
  const handlePageChange = (page, pageSize) => {
    loadProducts(page);
  };

  // 加载某个商品的关联视频
  const loadProductVideos = async (productId) => {
    if (productVideos[productId]) return; // 已有缓存
    try {
      const res = await axios.get(`${API_BASE}/gen2/product/${productId}`);
      if (res.data.success) {
        setProductVideos(prev => ({ ...prev, [productId]: res.data.data || [] }));
      }
    } catch (err) {
      console.error('加载关联视频失败', err);
    }
  };

  // 切换抖音发布状态
  const handleDouyinToggle = async (productId, checked) => {
    try {
      await axios.patch(`${API_BASE}/products/${productId}`, {
        posted_to_douyin: checked
      });
      // 更新本地状态
      setProducts(prev => prev.map(p =>
        p.id === productId ? { ...p, posted_to_douyin: checked } : p
      ));
      message.success(checked ? '已标记为已发布' : '已取消发布标记');
    } catch (err) {
      console.error('更新发布状态失败', err);
      message.error('更新失败');
    }
  };

  // 行展开/收起
  const handleRowExpand = (expanded, record) => {
    const keys = expanded ? [record.id] : [];
    setExpandedRowKeys(keys);
    if (expanded) {
      loadProductVideos(record.id);
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '商品图片',
      dataIndex: 'image_url',
      key: 'image_url',
      width: 120,
      render: (url, record) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Image
            src={url}
            width={60}
            height={60}
            style={{ objectFit: 'cover', borderRadius: 8 }}
            placeholder={<LoadingOutlined />}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
          />
          {record.cover_image_url && (
            <Tooltip title="封面图">
              <Image
                src={record.cover_image_url}
                width={60}
                height={60}
                style={{ objectFit: 'cover', borderRadius: 8, border: '2px solid #1890ff' }}
                placeholder={<LoadingOutlined />}
                fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
              />
            </Tooltip>
          )}
        </div>
      )
    },
    {
      title: '商品标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (text, record) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {text || <span style={{ color: '#999' }}>无标题</span>}
          </span>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleCopy(text || '', '商品标题')}
            style={{ flexShrink: 0, color: '#1890ff' }}
          />
        </span>
      )
    },
    {
      title: '商品链接',
      dataIndex: 'url',
      key: 'url',
      ellipsis: true,
      render: (url) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <a href={url} target="_blank" rel="noopener noreferrer" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <LinkOutlined /> {url}
          </a>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleCopy(url, '商品链接')}
            style={{ flexShrink: 0, color: '#1890ff' }}
          />
        </span>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (time) => time ? new Date(time).toLocaleString('zh-CN') : '-'
    },
    {
      title: '已发抖音',
      dataIndex: 'posted_to_douyin',
      key: 'posted_to_douyin',
      width: 100,
      align: 'center',
      render: (val, record) => (
        <Tooltip title={val ? '点击取消标记' : '点击标记为已发布'}>
          <Switch
            checked={val === 1 || val === true}
            checkedChildren={<CheckCircleOutlined />}
            unCheckedChildren={<VideoCameraOutlined />}
            onChange={(checked) => handleDouyinToggle(record.id, checked)}
          />
        </Tooltip>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button 
            type="primary" 
            size="small" 
            icon={<VideoCameraOutlined />}
            onClick={() => handleGenerateVideo(record)}
          >
            生成视频
          </Button>
          <Popconfirm
            title="确认删除此商品？"
            onConfirm={() => handleDelete(record.id)}
            okText="确认"
            cancelText="取消"
          >
            <Button danger size="small" icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className="page-shell">
      <div className="page-toolbar">
        <div>
          <div className="page-kicker">商品素材</div>
          <h1><ShoppingOutlined /> 商品管理</h1>
        </div>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
            添加商品
          </Button>
        </Space>
      </div>

      <div className="content-stack">
        {/* 商品列表 */}
        <Card title="商品列表" className="admin-card">
          {fetching && products.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <LoadingOutlined style={{ fontSize: 24 }} />
              <p style={{ marginTop: 8, color: '#999' }}>加载中...</p>
            </div>
          ) : products.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
              暂无商品，请先添加
            </div>
          ) : (
            <>
              <Table
                dataSource={products}
                columns={columns}
                rowKey="id"
                pagination={false}
                loading={fetching}
                scroll={{ x: 'max-content' }}
                expandedRowKeys={expandedRowKeys}
                onExpand={handleRowExpand}
                expandedRowRender={(record) => {
                  const videos = productVideos[record.id] || [];
                  if (videos.length === 0) {
                    return <div style={{ padding: '8px 0', color: '#999' }}>暂无关联视频</div>;
                  }
                  return (
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', padding: '8px 0' }}>
                      {videos.map(video => (
                        <div key={video.id} style={{ textAlign: 'center' }}>
                          <video
                            src={video.videoUrl}
                            controls
                            style={{ height: 120, borderRadius: 8, background: '#000' }}
                            preload="metadata"
                          />
                          <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                            编号：{video.id} {video.createdAt ? `｜${new Date(video.createdAt).toLocaleString('zh-CN')}` : ''}
                          </div>
                          <Space size={6} style={{ marginTop: 6 }}>
                            <Button
                              size="small"
                              icon={<PlayCircleOutlined />}
                              href={video.videoUrl}
                              target="_blank"
                            >
                              播放
                            </Button>
                            <Button
                              size="small"
                              type="primary"
                              icon={<MergeCellsOutlined />}
                              onClick={() => navigate(`/merge?historyId=${video.id}`)}
                            >
                              合并视频
                            </Button>
                          </Space>
                        </div>
                      ))}
                    </div>
                  );
                }}
              />
              <div style={{ marginTop: 16, textAlign: 'right' }}>
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                  showQuickJumper={false}
                  showTotal={(total) => `共 ${total} 条`}
                />
              </div>
            </>
          )}
        </Card>
      </div>

      {/* 添加商品弹窗 */}
      <Modal
        title="批量添加商品"
        open={modalVisible}
        onCancel={resetAddModal}
        maskClosable={!loading}
        closable={!loading}
        footer={[
          <Button key="cancel" onClick={resetAddModal} disabled={loading}>
            取消
          </Button>,
          <Button key="submit" type="primary" icon={<PlusOutlined />} onClick={handleAddProduct} loading={loading}>
            确认添加
          </Button>
        ]}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {productInputs.map((text, index) => (
            <div key={index} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <TextArea
                rows={4}
                placeholder={`请粘贴第 ${index + 1} 个商品链接或商品信息，例如：
1.58 10/02 RKJ:/ R@X.Zz 【抖音商城】
https://v.douyin.com/jSu0FjgJn7k/
夏日洋气新款穿搭一整套爆款高级感大方得体蕾丝衫阔腿裤两件套装
长按复制此条消息，打开抖音搜索，查看商品详情！`}
                value={text}
                disabled={loading}
                onChange={(e) => updateProductInput(index, e.target.value)}
              />
              {productInputs.length > 1 && (
                <Button
                  danger
                  type="text"
                  icon={<MinusCircleOutlined />}
                  disabled={loading}
                  onClick={() => removeProductInput(index)}
                  style={{ marginTop: 4, flexShrink: 0 }}
                />
              )}
            </div>
          ))}

          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={addProductInput}
            disabled={loading}
            block
          >
            +
          </Button>

          {addProgress.total > 0 && (
            <div>
              <div style={{ marginBottom: 8 }}>
                添加进度：{addProgress.completed}/{addProgress.total}
                {addProgress.completed === addProgress.total && `（成功 ${addProgress.success}，失败 ${addProgress.failed}）`}
              </div>
              <Progress
                percent={Math.round((addProgress.completed / addProgress.total) * 100)}
                status={addProgress.failed > 0 && addProgress.completed === addProgress.total ? 'exception' : (addProgress.completed === addProgress.total ? 'success' : 'active')}
              />
              {addProgress.results.length > 0 && (
                <div style={{ marginTop: 8, maxHeight: 120, overflowY: 'auto', fontSize: 12 }}>
                  {addProgress.results.map(item => (
                    <div key={item.index} style={{ color: item.status === 'success' ? '#52c41a' : '#ff4d4f' }}>
                      第 {item.index} 个：{item.status === 'success' ? '成功' : '失败'} - {item.title}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Space>
        <Alert
          message="说明"
          description="从抖音复制商品链接或商品信息粘贴到文本框。点击“+”可以继续添加输入框；确认后系统会按队列逐个提取商品链接、下载商品主图、生成卡通封面并写入商品列表。"
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      </Modal>
    </div>
  );
}

export default Product;
